import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEvent } from '../contexts/EventContext';
import { isCourseEvent } from '../lib/eventTypes';
import { useHeats } from '../hooks/useHeats';
import HeatGroupedView from '../components/heats/HeatGroupedView';
import UnassignedTeamsRail from '../components/heats/UnassignedTeamsRail';
import ImportHeatsDialog from '../components/heats/dialogs/ImportHeatsDialog';
import ExportPdfDialog from '../components/heats/dialogs/ExportPdfDialog';
import ReseedHeatsDialog from '../components/heats/dialogs/ReseedHeatsDialog';
import DeleteHeatsDialog from '../components/heats/dialogs/DeleteHeatsDialog';
import GenerateErrorDialog from '../components/heats/dialogs/GenerateErrorDialog';
import PublishHeatsDialog from '../components/heats/dialogs/PublishHeatsDialog';
import ConfirmPublishDialog from '../components/heats/dialogs/ConfirmPublishDialog';
import ScheduleNotifyDialog from '../components/heats/dialogs/ScheduleNotifyDialog';
import HeatFormDialog from '../components/heats/dialogs/HeatFormDialog';
import { useAutoSave } from '../hooks/useAutoSave';
import RedistributeHeatsDialog from '../components/heats/dialogs/RedistributeHeatsDialog';
import GenerateHeatsDialog, { type CourseWaveParams } from '../components/heats/dialogs/GenerateHeatsDialog';
import ConfirmActionDialog from '../components/heats/dialogs/ConfirmActionDialog';
import AutoAssignDialog, { type AutoAssignScope } from '../components/heats/dialogs/AutoAssignDialog';
import InsertBreakDialog from '../components/heats/dialogs/InsertBreakDialog';
import BulkStatusDialog from '../components/heats/dialogs/BulkStatusDialog';
import { estEvenementTermine } from '../components/heats/HeatStatusChip';
import { teamIdFromDragId } from '../components/heats/heatDragIds';
import HeatsToolbar from '../components/heats/HeatsToolbar';
import HeatsEmptyState from '../components/heats/HeatsEmptyState';
import MoveNodeDialog from '../components/heats/dialogs/MoveNodeDialog';
import type { MoveNodePlan, MoveNodeTargetDay } from '../components/heats/dialogs/MoveNodeDialog';
import type { PlanRow } from '../components/heats/heatPlanning';
import type {
  Heat,
  HeatParticipant,
  BreakTime,
  DaySchedule,
  GenerateParams,
  PublishPreview,
} from '../types/heats';
import { runGuidedChapter, getGuidedFlow, setGuidedFlow } from '../lib/tour';
// Seul le TYPE reste ici : le contexte de glisser-déposer vit dans la vue
// groupée, la page ne fournit plus que le gestionnaire.
import type { DropResult } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import type { HeatFormErrors } from '../components/heats/dialogs/HeatFormDialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import apiService from '../services/api.service';
import HeatsPdfService from '../services/heats-pdf.service';
import { heatsReseedService, type ReseedScope } from '../services/heats-reseed.service';

// Réordonnancement « optimiste » : réassigne les créneaux (start_time triés) et heat_number
// des heats listés dans `orderedIds` selon ce nouvel ordre (en réutilisant leurs propres
// créneaux, comme le backend). `setDate` force la date (déplacement inter-jours).
// Retourne un NOUVEAU tableau de heats (les autres inchangés) pour un affichage instantané ;
// les horaires exacts sont ensuite confirmés par le rechargement.
const optimisticReorderHeats = (allHeats: Heat[], orderedIds: string[], setDate?: string): Heat[] => {
  // Dédoublonner par sécurité : un id en double décalerait l'index des créneaux et
  // produirait des start_time `undefined` (→ crash au rendu).
  const seen = new Set<string>();
  const ids = orderedIds.filter(id => (seen.has(id) ? false : (seen.add(id), true)));
  const idSet = new Set(ids);
  const slots = allHeats
    .filter(h => idSet.has(h.id))
    .map(h => h.start_time)
    .sort((a, b) => (a || '').localeCompare(b || ''));
  const upd = new Map<string, Partial<Heat>>();
  ids.forEach((id, i) => {
    upd.set(id, {
      start_time: slots[i],
      heat_number: i + 1,
      ...(setDate !== undefined ? { date: setDate } : {}),
    });
  });
  return allHeats.map(h => (upd.has(h.id) ? { ...h, ...upd.get(h.id)! } : h));
};

const Heats: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightFromUrl = searchParams.get('highlight');
  // Force l'affichage (non cliquable) du bouton Publier pendant le tour guidé,
  // même sans heat publiable, pour que le spotlight ait une ancre.
  const [tourForcePublish, setTourForcePublish] = useState(false);

  // Utiliser le contexte global pour l'événement sélectionné
  const { events, selectedEventId: selectedEvent, selectedEvent: selectedEventData, setSelectedEventId } = useEvent();

  // Données du planning : source unique partagée (voir hooks/useHeats.ts).
  const {
    heats, setHeats,
    categories,
    wods,
    teams,
    heatParticipants, setHeatParticipants,
    loading, setLoading,
    loadHeats,
    loadAllParticipants,
    assignTeamToHeat,
    removeTeamFromHeat,
    autoAssignTeams,
    unassignAllTeams,
  } = useHeats(selectedEvent);
  // Événement passé : les vagues restées « en cours » redeviennent modifiables (2026-09-27).
  const eventOver = estEvenementTermine(selectedEventData?.date_end);

  // Créneaux d'inscription : le bouton d'ouverture par vague n'existe que si
  // l'événement fait choisir un créneau. Sinon, planning strictement inchangé.
  const [slotSelectionEnabled, setSlotSelectionEnabled] = useState(false);

  useEffect(() => {
    if (!selectedEvent) { setSlotSelectionEnabled(false); return; }
    let annule = false;
    apiService.getRegistrationSettings(selectedEvent)
      .then(r => { if (!annule) setSlotSelectionEnabled(!!r.data?.slot_selection_enabled); })
      // 404 = aucun paramètre d'inscription pour cet événement : pas de
      // créneaux, et surtout pas une erreur à montrer sur le planning.
      .catch(() => { if (!annule) setSlotSelectionEnabled(false); });
    return () => { annule = true; };
  }, [selectedEvent]);

  const toggleRegistrationOpen = useCallback(async (heat: Heat, ouvert: boolean) => {
    // Optimiste : l'organisateur ouvre souvent dix vagues d'affilée, un
    // aller-retour serveur par clic rendrait le geste poussif.
    setHeats(prev => prev.map(h =>
      h.id === heat.id ? { ...h, slot_open: ouvert } : h
    ));
    try {
      await apiService.updateHeat(heat.id, { slot_open: ouvert });
    } catch (error) {
      setHeats(prev => prev.map(h =>
        h.id === heat.id ? { ...h, slot_open: !ouvert } : h
      ));
      toast.error(t('common.error'));
    }
  }, [setHeats, t]);

  /**
   * Changer le statut d'UNE vague depuis sa pastille, au planning.
   *
   * Passe par l'endpoint de lot avec un seul id : il porte déjà les trois
   * règles qu'on veut — les trois statuts de planification seulement, le refus
   * d'une vague que le terrain a prise en main, et aucun email. Un second
   * endpoint « une vague » aurait fini par diverger de celui-là.
   */
  const changeHeatStatus = useCallback(async (heat: Heat, status: string) => {
    if (!selectedEvent) return;
    const avant = { status: heat.status, slot_open: heat.slot_open };

    // Optimiste : publier dix vagues d'affilée est le geste courant.
    // Quitter « publié » referme la porte, comme le fait le serveur — sinon
    // l'écran et la base se contrediraient le temps du rechargement.
    setHeats(prev => prev.map(h =>
      h.id === heat.id
        ? { ...h, status, slot_open: status === 'ready' ? h.slot_open : false }
        : h
    ));

    const revenir = () => setHeats(prev => prev.map(h =>
      h.id === heat.id ? { ...h, ...avant } : h
    ));

    try {
      const res = await apiService.bulkUpdateHeatStatus(selectedEvent, [heat.id], status);
      // Le serveur RENVOIE ce qu'il n'a pas touché plutôt que de l'écarter en
      // silence (LOG-195) : sans ce test, un refus passerait pour un succès.
      if (res.skipped?.length) {
        revenir();
        toast.error(t('heats.statusChip.refused'));
      }
    } catch (error) {
      revenir();
      toast.error(t('common.error'));
    }
  }, [selectedEvent, setHeats, t]);

  // Filtres du rail des équipes à placer (ex-écran Assignations).
  const [railWodFilter, setRailWodFilter] = useState<string>('all');
  const [railCategoryFilter, setRailCategoryFilter] = useState<string>('all');
  const [railSearch, setRailSearch] = useState<string>('');
  const [autoAssignDialogOpen, setAutoAssignDialogOpen] = useState(false);
  const [unassignAllDialogOpen, setUnassignAllDialogOpen] = useState(false);
  const [laneSwap, setLaneSwap] = useState<{
    heatId: string; team: HeatParticipant; cible: HeatParticipant; statut: string;
  } | null>(null);
  const [laneSwapSaving, setLaneSwapSaving] = useState(false);
  const [breakHeat, setBreakHeat] = useState<Heat | null>(null);

  const applyBreak = async (minutes: number) => {
    if (!breakHeat) return;
    await applyShift(breakHeat, minutes);
  };

  const applyShift = async (heat: Heat, minutes: number) => {
    if (!selectedEvent) return;
    try {
      setLoading(true);
      const res = await apiService.post(
        `/events/${selectedEvent}/heats/${heat.id}/shift-schedule`,
        { minutes },
      );
      setBreakHeat(null);
      // Un décalage négatif resserre le planning : c'est le retrait d'une pause,
      // et l'annoncer avec « décalé de -10 min » se lit mal.
      toast.success(t(minutes < 0 ? 'heats.insertBreak.removed' : 'heats.insertBreak.done', {
        count: res.data?.shifted_heats ?? 0,
        minutes: Math.abs(minutes),
      }));
      // Un heat DÉJÀ PUBLIÉ qui bouge = un horaire annoncé qui change. Le
      // serveur ne notifie jamais de lui-même : on réutilise la confirmation
      // existante plutôt que d'envoyer des emails en douce.
      const pending = res.data?.pending_schedule_notification;
      if (pending?.heat_ids?.length) {
        setScheduleNotify({ heatIds: pending.heat_ids, recipients: pending.recipients ?? 0 });
        setScheduleNotifyOpen(true);
      }
      await loadHeats();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  };

  /** Nombre d'équipes actuellement placées (conditionne « Tout désassigner »). */
  const assignedTeamsCount = Object.values(heatParticipants)
    .reduce((sum, list) => sum + list.length, 0);


  const runAutoAssign = async (scope: AutoAssignScope = {}) => {
    setAutoAssignDialogOpen(false);
    try {
      setLoading(true);
      const count = await autoAssignTeams(scope);
      toast.success(t('assignments.toasts.assignSuccess', { count }));
      await loadHeats();
      await loadAllParticipants();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  };

  const runLaneSwap = async () => {
    if (!laneSwap) return;
    const { heatId, team, cible } = laneSwap;
    setLaneSwapSaving(true);
    try {
      await apiService.put(`/heats/${heatId}/participants/${team.team_id}/lane`, {
        lane_number: cible.lane_number,
      });
      const r = await apiService.getHeatParticipants(heatId);
      setHeatParticipants(prev => ({ ...prev, [heatId]: r.data?.participants || [] }));
      toast.success(t('heats.laneSwap.done'));
      setLaneSwap(null);
    } catch (error: any) {
      toast.error(t('heats.laneSwap.error'), {
        description: error?.response?.data?.detail || error?.message,
      });
    } finally {
      setLaneSwapSaving(false);
    }
  };

  const runUnassignAll = async () => {
    setUnassignAllDialogOpen(false);
    try {
      setLoading(true);
      const count = await unassignAllTeams();
      toast.success(t('assignments.toasts.unassignAllSuccess', { count }));
      await loadHeats();
      await loadAllParticipants();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Publish heats
  const [publishLoading, setPublishLoading] = useState(false);
  const [highlightPublish, setHighlightPublish] = useState(highlightFromUrl === 'publish');
  const publishButtonRef = useRef<HTMLButtonElement>(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [selectedHeatsToPublish, setSelectedHeatsToPublish] = useState<string[]>([]);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false);
  const [publishWodFilter, setPublishWodFilter] = useState<string>('all');
  const [publishCategoryFilter, setPublishCategoryFilter] = useState<string>('all');
  // Étape de confirmation avant envoi des emails de publication
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [publishPreview, setPublishPreview] = useState<PublishPreview | null>(null);
  // Confirmation d'envoi des emails de changement d'horaire (après un réordonnancement / une édition d'un heat publié)
  const [scheduleNotifyOpen, setScheduleNotifyOpen] = useState(false);
  const [scheduleNotifyLoading, setScheduleNotifyLoading] = useState(false);
  const [scheduleNotify, setScheduleNotify] = useState<{ heatIds: string[]; recipients: number } | null>(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

  // Vue mode: 'table' (classique) ou 'grouped' (accordéons)
  // La vue groupée est l'ESPACE DE TRAVAIL : elle seule porte le rail des
  // équipes à placer, les couloirs et l'insertion de pause. La vue tableau
  // n'est qu'une lecture à plat.
  //
  // Elle doit donc être le défaut : l'entrée de menu « Assignations » a
  // disparu au profit de cet écran, et l'ouvrir sur le tableau rendait
  // l'assignation introuvable.

  // --- Tour guidé « publication des heats » (flow multi-pages Heats → Assignations → Heats) ---
  // Chapitre 0 (ici) : présentation des heats + « va sur Assignations ».
  // Chapitre 1 (page Assignations) : comment assigner les équipes.
  // Chapitre 2 (retour ici) : changement de statut (Prêt = publié/notifié) + bouton Publier.
  const HEATS_TOUR_DONE_KEY = 'logfit_heats_tour_v2';
  const PUBLISH_FLOW = 'heats_publish_v1';
  const tourLabels = useCallback(
    () => ({ next: t('tour.next'), prev: t('tour.prev'), done: t('tour.done'), progress: t('tour.progress') }),
    [t]
  );

  // Visite en UN SEUL chapitre.
  //
  // Elle en comptait trois, à cheval sur deux pages, parce que l'assignation
  // vivait ailleurs : le tour y renvoyait par le menu puis revenait pour la
  // publication. Les deux écrans n'en font plus qu'un — l'étape « va dans
  // Assignations » n'a plus d'ancre (l'entrée de menu n'existe plus) et le
  // rail des équipes se montre sur place.
  const startPublishFlow = useCallback(() => {
    setGuidedFlow({ flow: PUBLISH_FLOW, chapter: 0 });
    const finish = () => {
      setTourForcePublish(false);
      setGuidedFlow(null);
      try { localStorage.setItem(HEATS_TOUR_DONE_KEY, '1'); } catch { /* localStorage indisponible */ }
    };
    runGuidedChapter(
      [
        // L'étape « deux vues » n'a plus d'objet : il n'y en a plus qu'une.
        { title: t('heatsTour.welcome.title'), description: t('heatsTour.welcome.desc') },
        { element: '[data-tour="heats-grouped"]', title: t('heatsTour.grouped.title'), description: t('heatsTour.grouped.desc') },
        { element: '[data-tour="assign-teams"]', title: t('assignmentsTour.teams.title'), description: t('assignmentsTour.teams.desc') },
        // `before` retiré : l'ancre ne désigne plus un champ caché dans une
        // fenêtre, mais la pastille de la première ligne, toujours à l'écran.
        { element: '[data-tour="heats-status"]', title: t('heatsTour.status.title'), description: t('heatsTour.status.desc') },
        { element: '[data-tour="heats-publish-btn"]', title: t('heatsTour.publish.title'), description: t('heatsTour.publish.desc'), before: () => setTourForcePublish(true) },
        { title: t('heatsTour.done.title'), description: t('heatsTour.done.desc') },
      ],
      tourLabels(),
      { onComplete: finish, onExit: finish }
    );
  }, [t, tourLabels]);


  // Auto-lancement (desktop, 1re fois, quand des heats existent).
  const heatsTourStartedRef = useRef(false);
  const startPublishFlowRef = useRef(startPublishFlow);
  startPublishFlowRef.current = startPublishFlow;
  useEffect(() => {
    if (heatsTourStartedRef.current) return;
    if (window.innerWidth < 640) return;
    if (heats.length === 0) return;
    if (localStorage.getItem(HEATS_TOUR_DONE_KEY)) return;
    // Un flow laissé en cours par l'ancienne visite en trois chapitres ne doit
    // pas bloquer celle-ci : elle se joue désormais d'un seul tenant.
    const flow = getGuidedFlow();
    if (flow && flow.flow !== PUBLISH_FLOW) return;
    heatsTourStartedRef.current = true;
    const timer = setTimeout(() => startPublishFlowRef.current(), 800);
    return () => clearTimeout(timer);
  }, [heats.length]);
  const startHeatsTour = startPublishFlow; // relance manuelle via le bouton « ? »


  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingHeat, setEditingHeat] = useState<Heat | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [pdfExportDialogOpen, setPdfExportDialogOpen] = useState(false);

  const [reseedDialogOpen, setReseedDialogOpen] = useState(false);
  const [selectedReseedDate, setSelectedReseedDate] = useState<string>('');
  const [futureDates, setFutureDates] = useState<string[]>([]);
  const [reseedLoading, setReseedLoading] = useState(false);

  // Signatures : consultation déplacée sur la page Résultats (revue UX
  // 2026-08-03) — c'est une pièce justificative de score, pas un attribut
  // du heat.

  // Redistribution des heats
  const [redistributeDialogOpen, setRedistributeDialogOpen] = useState(false);
  const [redistributeLoading, setRedistributeLoading] = useState(false);
  const [redistributePreview, setRedistributePreview] = useState<any>(null);
  const [redistributeParams, setRedistributeParams] = useState({
    new_lanes_count: 6,
    allow_category_mixing: false,
    restart_time: '',
    new_heat_gap: ''
  });

  const [courseWaveParams, setCourseWaveParams] = useState<CourseWaveParams>({
    wave_interval_minutes: 5,
    participants_per_wave: 50,
  });
  // Ordre voulu des catégories à la génération des vagues (liste d'IDs de catégories)
  const [courseCategoryOrder, setCourseCategoryOrder] = useState<string[]>([]);
  // Catégories retenues pour la génération de vagues ([] = toutes).
  const [selectedCategoriesForGeneration, setSelectedCategoriesForGeneration] = useState<string[]>([]);

  const moveCategoryOrder = (index: number, dir: -1 | 1) => {
    setCourseCategoryOrder(prev => {
      const arr = [...prev];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      return arr;
    });
  };

  /**
   * Jour 1 du planning : la date de l'ÉVÉNEMENT, pas celle d'aujourd'hui.
   *
   * Le formulaire s'initialisait sur `new Date()`. L'organisateur qui ne
   * touchait pas au champ datait tout son planning du jour où il l'a créé —
   * un événement du 29 août planifié le 17 : des heats hors de l'événement,
   * invisibles dans sa plage, et une date fausse dans l'email d'annonce.
   *
   * On ne demande pas ce qu'on sait déjà. Le recalage n'a lieu qu'au CHANGEMENT
   * d'événement (mémorisé dans `scheduleInitPourEvent`) : rouvrir la fenêtre
   * pour le même événement conserve les jours ajoutés et les pauses saisies.
   */
  const scheduleInitPourEvent = React.useRef<string | null>(null);
  useEffect(() => {
    if (!selectedEvent) return;
    if (scheduleInitPourEvent.current === selectedEvent) return;

    const debut = selectedEventData?.date_start?.substring(0, 10);
    if (!debut) return;  // Sans date d'événement, on ne sait pas mieux que le formulaire.

    scheduleInitPourEvent.current = selectedEvent;
    setMultiDaySchedule([{
      date: debut,
      start_time: '09:00',
      end_time: '18:00',
      wod_ids: [],
      breaks: [],
    }]);
  }, [selectedEvent, selectedEventData]);

  // À l'ouverture du dialog de génération (mode course), initialiser/mettre à jour
  // l'ordre des catégories depuis la liste réelle, en préservant l'ordre déjà choisi.
  useEffect(() => {
    if (!generateDialogOpen) return;
    // Course comme CrossFit : l'ordre suit les catégories de l'événement.
    const catIds = categories.map(c => c.id);
    setCourseCategoryOrder(prev => {
      const kept = prev.filter(id => catIds.includes(id));
      const added = catIds.filter(id => !kept.includes(id));
      return [...kept, ...added];
    });
  }, [generateDialogOpen, selectedEvent, events, categories]);

  // Dialog suppression tous les heats
  const [deleteHeatsOpen, setDeleteHeatsOpen] = useState(false);
  const [deleteHeatsLoading, setDeleteHeatsLoading] = useState(false);
  const [heatToDelete, setHeatToDelete] = useState<string | null>(null);
  const [deletingHeat, setDeletingHeat] = useState(false);
  const [redistributeConfirmOpen, setRedistributeConfirmOpen] = useState(false);
  const [heatFormErrors, setHeatFormErrors] = useState<HeatFormErrors>({});

  // Dialog erreur génération
  const [generateErrorDialogOpen, setGenerateErrorDialogOpen] = useState(false);
  const [generateError, setGenerateError] = useState<{
    error: string;
    total_heats_needed: number;
    total_heats_placed: number;
    shortage_heats: number;
    suggestions: string[];
  } | null>(null);

  // Course sélectionnée pour la génération (événements course uniquement)
  const [selectedCourseForGeneration, setSelectedCourseForGeneration] = useState<string>('');
  const [availableCoursesForGeneration, setAvailableCoursesForGeneration] = useState<{id: string, name: string}[]>([]);

  const [heatForm, setHeatForm] = useState({
    heat_number: '',
    heat_name: '',
    heat_date: '',
    start_time: '',
    end_time: '',
    max_participants: 18,
    wod_id: '',
    category_id: '',
  });



  const [generateParams, setGenerateParams] = useState<GenerateParams>({
    lanes_available: 10,
    wod_duration: 20,
    heat_gap: 5,
    category_gap: 5,
    wod_gap: 10,
    start_time: '09:00',
    end_time: '18:00',
    use_wod_time_caps: true,
    schedule_type: 'single_day',
    breaks: []
  });

  const [multiDaySchedule, setMultiDaySchedule] = useState<DaySchedule[]>([
    {
      date: new Date().toISOString().split('T')[0],
      start_time: '09:00',
      end_time: '18:00',
      wod_ids: [],
      breaks: []
    }
  ]);

  /**
   * Lâcher d'une ÉQUIPE : rail → couloir, couloir → rail, couloir → couloir.
   *
   * Le déplacement des heats et des blocs ne passe plus par ici : la vue
   * traduit elle-même le geste en un ordre chronologique complet de la journée
   * et appelle `reorderDay`. Les trois branches d'origine (HEAT, NODE/wod,
   * NODE/catégorie) encodaient la hiérarchie jour ⊃ WOD ⊃ catégorie, celle-là
   * même qui interdisait qu'un WOD apparaisse deux fois dans une journée.
   */
  const handleTeamDrop = useCallback(async (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    if (result.type !== 'TEAM') return;

    {
      // Extraire heatId du droppableId (format: "heat-{heatId}")
      const sourceHeatId = source.droppableId.replace('heat-', '');
      const targetHeatId = destination.droppableId.replace('heat-', '');
      // `draggableId` est préfixé par son CONTENEUR : `rail-team-<id>` dans le
      // rail, `lane-<heatId>-team-<id>` dans un couloir. Sans ces préfixes, la
      // même équipe portait le même identifiant aux deux endroits — et une
      // équipe placée sur un WOD reste « à placer » pour les autres, donc les
      // deux cartes coexistent dès qu'une heat est dépliée. dnd résolvait
      // alors l'identifiant vers l'une des deux au hasard : saisir la carte du
      // rail faisait bouger celle du couloir (relevé par Bryan, 2026-08-16).
      const teamId = teamIdFromDragId(draggableId);

      // --- Depuis le rail des équipes à placer : assignation ---
      if (source.droppableId === 'unassigned' && destination.droppableId.startsWith('heat-')) {
        const heat = heats.find(h => h.id === targetHeatId);
        if (!heat) return;
        // Mêmes garde-fous que l'ancien écran Assignations : un heat terminé
        // ne se remplit plus, et on ne dépasse pas le nombre de couloirs.
        if (heat.status === 'finished') {
          toast.warning(t('assignments.toasts.heatFinishedAssign'));
          return;
        }
        const current = heatParticipants[targetHeatId] || [];
        if (current.some(p => p.team_id === teamId)) {
          toast.warning(t('assignments.toasts.alreadyAssigned'));
          return;
        }
        // WOD éliminatoire (LOG-219) : réservé aux équipes ayant terminé le
        // prérequis. Le serveur refuse aussi (409) — ici on le dit avant.
        const heatWod = wods.find(w => w.id === heat.wod_id);
        if (heatWod?.requires_finished_wod_id && !(heatWod.qualified_team_ids || []).includes(teamId)) {
          toast.warning(t('assignments.toasts.notQualified', { name: heatWod.requires_finished_wod_name || '' }));
          return;
        }
        if (current.length >= heat.max_participants) {
          toast.warning(t('assignments.toasts.heatFull', { count: current.length, max: heat.max_participants }));
          return;
        }
        try {
          setLoading(true);
          // Une équipe ne court qu'une fois par WOD : si elle est déjà placée
          // sur un AUTRE heat du même WOD, on l'en retire — sinon elle serait
          // inscrite deux fois à la même épreuve.
          for (const [otherHeatId, others] of Object.entries(heatParticipants)) {
            if (otherHeatId === targetHeatId) continue;
            const otherHeat = heats.find(h => h.id === otherHeatId);
            if (otherHeat?.wod_id && otherHeat.wod_id === heat.wod_id
                && others.some(p => p.team_id === teamId)) {
              await removeTeamFromHeat(otherHeatId, teamId);
            }
          }
          await assignTeamToHeat(targetHeatId, teamId);
          await loadHeats();
          await loadAllParticipants();
        } catch (error: any) {
          toast.error(error.message || t('heats.toasts.moveError'));
        } finally {
          setLoading(false);
        }
        return;
      }

      // --- Vers le rail : retrait du heat ---
      if (destination.droppableId === 'unassigned' && source.droppableId.startsWith('heat-')) {
        const heat = heats.find(h => h.id === sourceHeatId);
        if (heat?.status === 'finished') {
          toast.warning(t('assignments.toasts.heatFinishedRemove'));
          return;
        }
        const teamName = (heatParticipants[sourceHeatId] || []).find(p => p.team_id === teamId)?.team_name;
        try {
          setLoading(true);
          await removeTeamFromHeat(sourceHeatId, teamId);
          await loadHeats();
          await loadAllParticipants();
          // Retirer d'un heat se défait : un toast « Annuler » plutôt qu'une
          // confirmation avant le geste. Le serveur replace l'équipe au
          // premier couloir libre — celui qu'elle vient de quitter.
          toast(t('heats.toasts.teamRemoved', { name: teamName || t('nav.teams') }), {
            action: {
              label: t('heats.toasts.undo'),
              onClick: async () => {
                try {
                  await assignTeamToHeat(sourceHeatId, teamId);
                  await loadHeats();
                  await loadAllParticipants();
                } catch (err: any) {
                  toast.error(err.message || t('heats.toasts.moveError'));
                }
              },
            },
          });
        } catch (error: any) {
          toast.error(error.message || t('heats.toasts.moveError'));
        } finally {
          setLoading(false);
        }
        return;
      }

      // Même heat : échanger deux couloirs, ENREGISTRÉ (avant, l'échange ne
      // changeait que l'écran — BoL, 2026-10-04 : un organisateur a annoncé
      // un ordre qui n'existait que sur sa tablette). On confirme d'abord : le
      // changement part tout de suite, et en direct si la heat est en cours.
      if (sourceHeatId === targetHeatId) {
        const parCouloir = [...(heatParticipants[sourceHeatId] || [])]
          .sort((a, b) => a.lane_number - b.lane_number);
        const moved = parCouloir[source.index];
        const cible = parCouloir[destination.index];
        if (!moved || !cible || moved.team_id === cible.team_id) return;
        const heat = heats.find(h => h.id === sourceHeatId);
        setLaneSwap({
          heatId: sourceHeatId,
          team: moved,
          cible,
          statut: heat?.status ?? 'scheduled',
        });
        return;
      }

      // Déplacer vers un autre heat
      try {
        setLoading(true);
        await apiService.moveTeamToHeat(sourceHeatId, teamId, targetHeatId, destination.index + 1);

        // Recharger les heats pour mettre à jour participant_count.
        // Via apiService (et non un fetch brut) : lui seul porte l'intercepteur
        // de rafraîchissement du jeton.
        const heatsResponse = await apiService.get(`/heats?event_id=${selectedEvent}`);
        {
          const heatsData = heatsResponse.data || [];
          setHeats(heatsData);

          // Recharger les participants des heats affectés
          const participantsMap: Record<string, HeatParticipant[]> = { ...heatParticipants };
          for (const heatId of [sourceHeatId, targetHeatId]) {
            try {
              const pResponse = await apiService.getHeatParticipants(heatId);
              participantsMap[heatId] = pResponse.data?.participants || [];
            } catch {
              participantsMap[heatId] = [];
            }
          }
          setHeatParticipants(participantsMap);
          // Pas de toast : l'équipe qui apparaît dans son nouveau couloir EST le
          // retour. Seule l'erreur mérite d'être annoncée.
        }
      } catch (error: any) {
        console.error('Erreur déplacement équipe:', error);
        toast.error(t('heats.toasts.moveError'), {
          description: error?.response?.data?.detail || t('heats.toasts.moveErrorDesc'),
        });
      } finally {
        setLoading(false);
      }
    }

  }, [heats, selectedEvent, heatParticipants, t,
      setHeats, setHeatParticipants, setLoading,
      assignTeamToHeat, removeTeamFromHeat, loadHeats, loadAllParticipants]);

  /**
   * Nouvel ordre d'une journée, calculé par la vue quelle que soit la
   * granularité saisie (une heat, un bloc de catégorie, un bloc de WOD).
   *
   * Le serveur renumérote et recalcule les horaires : en CrossFit il enchaîne
   * `time_cap + gap`, en Course il réassigne les créneaux existants triés. On
   * lui envoie donc la SUITE, pas une intention.
   */
  const reorderDay = useCallback(async (orderedHeatIds: string[]) => {
    if (!selectedEvent) return;
    const heatOrders = orderedHeatIds.map((id, i) => ({ heat_id: id, new_position: i + 1 }));
    const previous = heats;
    // Optimiste : le nouvel ordre s'affiche tout de suite, le serveur confirme.
    setHeats(optimisticReorderHeats(heats, orderedHeatIds));
    try {
      const res = await apiService.reorderHeats(selectedEvent, heatOrders, true);
      const fresh = await apiService.get(`/heats?event_id=${selectedEvent}`);
      if (fresh.data) setHeats(fresh.data);
      // Pas de toast : le nouvel ordre à l'écran est le retour.
      maybePromptScheduleNotify(res.data);
    } catch (error) {
      setHeats(previous);
      console.error('Erreur réordonnancement:', error);
      toast.error(t('heats.toasts.reorderError'), {
        description: t('heats.toasts.reorderErrorDesc'),
      });
    }
  }, [heats, selectedEvent, setHeats, t]);


  // ---- Déplacer vers un autre jour ----------------------------------------
  //
  // Le glisser-déposer ne peut PAS le faire : une seule journée est montée à la
  // fois, on ne glisse pas vers un onglet masqué. Cette fenêtre n'est donc pas
  // un doublon du geste — c'est le seul chemin pour changer de jour.
  //
  // Elle ne demande QUE le jour : une position d'arrivée n'aurait plus de sens
  // maintenant qu'un même WOD peut apparaître plusieurs fois dans une journée.
  // Le bloc atterrit en fin de journée, et se replace ensuite au glisser.

  const [pendingMove, setPendingMove] =
    useState<{ heatIds: string[]; plan: MoveNodePlan } | null>(null);

  /** Jours proposés : ceux qui portent des heats, plus toute la plage de l'événement. */
  const moveDays = React.useMemo<MoveNodeTargetDay[]>(() => {
    const dates = new Set<string>(
      heats.map(h => h.date).filter((d): d is string => !!d && d !== 'no-date'),
    );
    const start = selectedEventData?.date_start?.substring(0, 10);
    const end = selectedEventData?.date_end?.substring(0, 10) || start;
    if (start && end) {
      for (let d = new Date(start); d <= new Date(end); d.setDate(d.getDate() + 1)) {
        dates.add(d.toISOString().substring(0, 10));
      }
    }
    return [...dates].sort().map(date => ({
      date,
      dateLabel: new Date(date).toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long',
      }),
    }));
  }, [heats, selectedEventData]);

  /** Un bloc entier (en-tête de WOD ou de catégorie) part vers un autre jour. */
  const openMoveBlockToDay = useCallback((row: PlanRow, date: string) => {
    if (row.heatIds.length === 0) return;
    setPendingMove({
      heatIds: row.heatIds,
      plan: {
        kind: row.kind === 'wod' ? 'wod' : 'category',
        label: row.label,
        sourceDate: date,
        heatCount: row.heatIds.length,
        teamCount: row.heatIds.reduce((n, id) => n + (heatParticipants[id]?.length ?? 0), 0),
      },
    });
  }, [heatParticipants]);

  /** Une heat seule part vers un autre jour. */
  const openMoveHeatToDay = useCallback((heat: Heat) => {
    setPendingMove({
      heatIds: [heat.id],
      plan: {
        kind: 'heat',
        label: `${t('heats.heatNumber', { number: heat.heat_number })} — ${heat.start_time?.substring(0, 5) || ''}`,
        sourceDate: heat.date || 'no-date',
        heatCount: 1,
        teamCount: heatParticipants[heat.id]?.length ?? 0,
      },
    });
  }, [heatParticipants, t]);

  const confirmMove = useCallback(async (date: string) => {
    const pending = pendingMove;
    setPendingMove(null);
    if (!pending || !selectedEvent || date === pending.plan.sourceDate) return;

    try {
      setLoading(true);
      for (const id of pending.heatIds) {
        await apiService.moveHeatToDay(selectedEvent, id, date);
      }
      // Le bloc arrive en fin de journée : on renvoie l'ordre complet du jour
      // cible pour que le serveur lui attribue de vrais créneaux.
      const fresh = await apiService.get(`/heats?event_id=${selectedEvent}`);
      const all: Heat[] = fresh.data || [];
      setHeats(all);

      const movedSet = new Set(pending.heatIds);
      const dayHeats = all.filter(h => (h.date || 'no-date') === date);
      const ordered = [
        ...dayHeats.filter(h => !movedSet.has(h.id))
          .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
          .map(h => h.id),
        ...pending.heatIds.filter(id => dayHeats.some(h => h.id === id)),
      ];
      if (ordered.length) await reorderDay(ordered);
      // Pas de toast : la fenêtre se ferme et la heat apparaît sur son nouveau jour.
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.message);
      await loadHeats();
    } finally {
      setLoading(false);
    }
  }, [pendingMove, selectedEvent, setHeats, setLoading, reorderDay, loadHeats]);

  // Les événements sont chargés depuis EventContext

  // Chargements et état des données : voir hooks/useHeats.ts

  // Pré-remplir lanes_available depuis les heats existants
  useEffect(() => {
    if (heats.length > 0 && heats[0]?.max_participants) {
      setGenerateParams(prev => ({ ...prev, lanes_available: heats[0].max_participants }));
    }
  }, [heats]);

  useEffect(() => {
    if (heats.length > 0) {
      // Récupérer toutes les dates avec des heats non terminés (pour le reseed)
      const dates = Array.from(new Set(
        heats
          .filter(h => h.date && h.status !== 'finished' && h.status !== 'completed')
          .map(h => h.date)
      )).sort();
      setFutureDates(dates as string[]);
      if (dates.length > 0 && !selectedReseedDate) {
        setSelectedReseedDate(dates[0] as string);
      }
    }
  }, [heats, selectedReseedDate]);

  // Charger les participants quand on passe en vue groupée
  useEffect(() => {
    if (heats.length > 0) {
      loadAllParticipants();
    }
  }, [heats, loadAllParticipants]);

  // Highlight et scroll vers le bouton Publier si highlight=publish dans l'URL
  useEffect(() => {
    if (highlightPublish && publishButtonRef.current && heats.length > 0) {
      setTimeout(() => {
        publishButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      // Retirer le highlight après 5s
      const timer = setTimeout(() => {
        setHighlightPublish(false);
        // Nettoyer l'URL
        searchParams.delete('highlight');
        setSearchParams(searchParams, { replace: true });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightPublish, heats, searchParams, setSearchParams]);

  const handleGenerateHeats = async () => {
    if (!selectedEvent) return;

    const eventData = events.find(e => e.id === selectedEvent);
    const isCourseModeEvent = isCourseEvent(eventData?.event_type);

    setLoading(true);
    try {
      const requestBody = isCourseModeEvent
        ? {
            event_id: selectedEvent,
            mode: 'course',
            wave_interval_minutes: courseWaveParams.wave_interval_minutes,
            participants_per_wave: courseWaveParams.participants_per_wave,
            // Planification prévisionnelle : absents, le serveur dimensionne
            // sur les inscrits — le comportement d'origine.
            custom_team_count: courseWaveParams.custom_team_count,
            team_distribution_mode: courseWaveParams.team_distribution_mode,
            days: multiDaySchedule,
            category_order: courseCategoryOrder,
            // Vide = toutes (comportement d'origine). L'organisateur ouvre
            // souvent ses inscriptions par vagues successives et ne planifie
            // qu'une partie des catégories à la fois.
            category_ids: selectedCategoriesForGeneration,
            wod_id: selectedCourseForGeneration || null
          }
        : {
            event_id: selectedEvent,
            mode: 'crossfit',
            ...generateParams,
            schedule_type: 'multi_day',
            days: multiDaySchedule,
            // Vide = toutes, dans l'ordre d'affichage — comme en course.
            category_ids: selectedCategoriesForGeneration,
            category_order: courseCategoryOrder
          };

      const response = await apiService.generateHeats(requestBody);
      toast.success(response.data?.message || t('heats.toasts.generated'));
      setGenerateDialogOpen(false);
      loadHeats();
    } catch (error: any) {
      // Le refus de planification (400) porte un détail structuré : il a sa
      // propre fenêtre, qui dit combien d'équipes ne rentrent pas et pourquoi.
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error) {
        setGenerateError(detail);
        setGenerateErrorDialogOpen(true);
        return;
      }
      console.error('Erreur génération heats:', error);
      toast.error(typeof detail === 'string' ? detail : (error.message || t('common.error')));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHeat = (heatId: string) => setHeatToDelete(heatId);

  const confirmDeleteHeat = async () => {
    if (!heatToDelete) return;
    setDeletingHeat(true);
    try {
      await apiService.deleteHeat(heatToDelete);
      setHeatToDelete(null);
      loadHeats();
    } catch (error: any) {
      console.error('Erreur suppression heat:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setDeletingHeat(false);
    }
  };

  /**
   * Ouvre la génération. En mode Course, la liste des épreuves est chargée à
   * l'ouverture : elle n'est utile qu'ici, et la précharger imposerait un appel
   * à toute page qui n'ouvrira jamais cette fenêtre.
   */
  const openGenerateDialog = () => {
    setGenerateDialogOpen(true);
    if (estEvenementCourse && selectedEvent) {
      apiService.getWODs(selectedEvent).then(response => {
        const list = response.data.wods || response.data || [];
        const courses = list.map((w: any) => ({ id: w.id, name: w.name }));
        setAvailableCoursesForGeneration(courses);
        // Une seule course : c'est celle qu'on court. On ne demande pas ce
        // qu'on sait déjà — laissé vide par défaut, l'organisateur générait
        // des vagues sans épreuve sans s'en apercevoir, et son planning
        // s'ouvrait sur un « Sans WOD » (relevé par Bryan le 2026-08-17).
        // Au-delà d'une, le choix lui appartient : on ne devine pas.
        if (courses.length === 1 && !selectedCourseForGeneration) {
          setSelectedCourseForGeneration(courses[0].id);
        }
      }).catch(console.error);
    }
  };

  /** Ouvre la redistribution, préremplie sur le planning existant. */
  const openRedistributeDialog = () => {
    setRedistributePreview(null);
    setRedistributeParams({
      new_lanes_count: heats[0]?.max_participants || 6,
      allow_category_mixing: false,
      restart_time: '',
      new_heat_gap: '',
    });
    setRedistributeDialogOpen(true);
  };

  /**
   * Suppression d'une sélection de heats. Le serveur refuse les heats en cours
   * ou terminées et les RENVOIE : on les annonce plutôt que de laisser croire
   * que tout est parti.
   */
  // Libellés d'une heat dans les fenêtres en lot (statut, suppression).
  const heatWodName = useCallback((h: Heat) =>
    wods.find(w => w.id === h.wod_id)?.name || h.wod_name || t('heats.noWod'), [wods, t]);
  const heatCategoryName = useCallback((h: Heat) => (h.is_multi_category && h.category_names?.length)
    ? h.category_names.join(' / ')
    : (h.category_name
      || categories.find(c => c.id === h.category_id)?.name
      || t('assignments.noCategory')), [categories, t]);

  const handleDeleteHeats = async (heatIds: string[]) => {
    if (!selectedEvent) return;
    setDeleteHeatsLoading(true);
    try {
      const res = await apiService.deleteHeats(selectedEvent, heatIds);
      toast.success(t('heats.deleteHeats.done', { count: res.deleted_count }));
      if (res.skipped.length > 0) {
        toast.warning(t('heats.deleteHeats.skipped', { count: res.skipped.length }));
      }
      setDeleteHeatsOpen(false);
      await loadHeats();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setDeleteHeatsLoading(false);
    }
  };

  // Heats publiables = toutes les vagues encore en planification.
  // ⚠️ PAS de condition `participant_count > 0` : elle était écrite pour le flux
  // classique (assigner les équipes, PUIS publier), et les créneaux d'inscription
  // inversent cet ordre — une vague est publiée VIDE pour être proposée au choix,
  // l'athlète s'y place ensuite. Le compteur à zéro faisait disparaître le bouton
  // « Publier » et ne laissait que « ⋯ → Changer le statut… » comme contournement.
  // Aucun risque d'envoi : publier une vague sans inscrit n'écrit aucun email,
  // le serveur ne notifie que les participants réellement présents.
  const publishableHeats = heats.filter(h => h.status === 'scheduled');

  /**
   * Changement de statut en lot. Le serveur refuse les heats en cours ou
   * terminées et les RENVOIE : on les annonce plutôt que de laisser croire que
   * tout a été modifié.
   */
  const handleBulkStatus = async (heatIds: string[], status: string) => {
    if (!selectedEvent) return;
    setBulkStatusLoading(true);
    try {
      const res = await apiService.bulkUpdateHeatStatus(selectedEvent, heatIds, status);
      toast.success(t('heats.bulkStatus.done', { count: res.updated_count }));
      if (res.skipped.length > 0) {
        toast.warning(t('heats.bulkStatus.skipped', { count: res.skipped.length }));
      }
      setBulkStatusOpen(false);
      await loadHeats();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setBulkStatusLoading(false);
    }
  };

  const handleOpenPublishDialog = () => {
    if (publishableHeats.length === 0) {
      toast.info(t('heats.publishDialog.nothingToPublish'));
      return;
    }
    // Rien de pré-sélectionné : publier envoie un email à chaque participant,
    // et tout cocher d'avance fait de l'envoi le geste par défaut. C'est aussi
    // ce qui protège les WODs qu'on ne veut justement PAS annoncer — une
    // finale, dont les équipes ne sont connues qu'à la fin.
    setPublishWodFilter('all');
    setPublishCategoryFilter('all');
    setSelectedHeatsToPublish([]);
    setPublishDialogOpen(true);
  };

  // Heats filtrés pour le dialog de publication
  const filteredPublishableHeats = publishableHeats.filter(heat => {
    if (publishWodFilter !== 'all' && heat.wod_id !== publishWodFilter) return false;
    // Une heat de groupe n'a pas de `category_id` : son entrée de filtre est son
    // groupe (même grammaire que le planning, HeatGroupedView).
    if (publishCategoryFilter !== 'all'
      && heat.category_id !== publishCategoryFilter
      && heat.category_group_id !== publishCategoryFilter) return false;
    return true;
  });

  // Sélection EFFECTIVE = sélection ∩ heats actuellement affichés (filtre appliqué).
  // On ne publie jamais un heat masqué par le filtre : le compteur et l'action de
  // publication s'appuient sur cette liste, pas sur la sélection brute.
  const effectiveSelectedHeatIds = filteredPublishableHeats
    .filter(h => selectedHeatsToPublish.includes(h.id))
    .map(h => h.id);

  // WODs et catégories uniques pour les filtres
  const uniqueWodsInPublishable = Array.from(
    new Map(publishableHeats.filter(h => h.wod_id).map(h => [h.wod_id, { id: h.wod_id, name: h.wod_name }])).values()
  );
  const uniqueCategoriesInPublishable = Array.from(
    new Map(publishableHeats
      .filter(h => h.category_group_id || h.category_id)
      .map(h => {
        const id = (h.category_group_id || h.category_id) as string;
        const name = (h.is_multi_category && h.category_names?.length)
          ? h.category_names.join(' / ')
          : h.category_name;
        return [id, { id, name }];
      })).values()
  );

  const toggleHeatSelection = (heatId: string) => {
    setSelectedHeatsToPublish(prev =>
      prev.includes(heatId)
        ? prev.filter(id => id !== heatId)
        : [...prev, heatId]
    );
  };

  const toggleAllHeatsSelection = () => {
    const filteredIds = filteredPublishableHeats.map(h => h.id);
    const allFilteredSelected = filteredIds.every(id => selectedHeatsToPublish.includes(id));

    if (allFilteredSelected) {
      // Désélectionner uniquement les heats filtrés
      setSelectedHeatsToPublish(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      // Ajouter tous les heats filtrés à la sélection
      setSelectedHeatsToPublish(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  // Étape 1 : demander l'aperçu (qui va être prévenu par email) AVANT tout envoi
  const handleRequestPublish = async () => {
    if (!selectedEvent || effectiveSelectedHeatIds.length === 0) return;

    setPreviewLoading(true);
    try {
      const response = await apiService.publishHeatsPreview(selectedEvent, effectiveSelectedHeatIds);
      setPublishPreview(response.data as PublishPreview);
      setConfirmPublishOpen(true);
    } catch (error: any) {
      console.error('Erreur aperçu publication:', error);
      toast.error(`Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Étape 2 : envoi réel après confirmation explicite
  const confirmPublishHeats = async () => {
    if (!selectedEvent || effectiveSelectedHeatIds.length === 0) return;

    setPublishLoading(true);
    try {
      const response = await apiService.publishHeats(selectedEvent, effectiveSelectedHeatIds);
      const data = response.data;
      // « en cours d'envoi » et non « notifiés » : la réponse revient tout de
      // suite, les emails partent derrière elle.
      toast.success(t('heats.toasts.published', {
        heats: data.published_count,
        count: data.recipients_count ?? data.notified_count ?? 0,
      }));
      loadHeats();
      setHighlightPublish(false);
      setConfirmPublishOpen(false);
      setPublishDialogOpen(false);
      setSelectedHeatsToPublish([]);
      setPublishPreview(null);
    } catch (error: any) {
      console.error('Erreur publication heats:', error);
      toast.error(`Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setPublishLoading(false);
    }
  };

  // Si une action (réordonnancement / édition) a modifié l'horaire de heats DÉJÀ
  // publiés, proposer d'envoyer l'email de changement d'horaire (jamais automatique).
  const maybePromptScheduleNotify = (data: any) => {
    const pending = data?.pending_schedule_notification;
    if (pending && pending.recipients > 0 && pending.heat_ids?.length) {
      setScheduleNotify({ heatIds: pending.heat_ids, recipients: pending.recipients });
      setScheduleNotifyOpen(true);
    }
  };

  const confirmScheduleNotify = async () => {
    if (!selectedEvent || !scheduleNotify) return;
    setScheduleNotifyLoading(true);
    try {
      const res = await apiService.notifyScheduleChange(selectedEvent, scheduleNotify.heatIds);
      toast.success(t('heats.scheduleNotify.sent', { count: res.data.notified_count }));
    } catch (error: any) {
      console.error('Erreur notification changement horaire:', error);
      toast.error(`Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setScheduleNotifyLoading(false);
      setScheduleNotifyOpen(false);
      setScheduleNotify(null);
    }
  };

  /** Marque les champs manquants SOUS le champ (aria-invalid) et le dit une fois en toast. */
  const validateHeatForm = (): boolean => {
    const errors: HeatFormErrors = {};
    if (!heatForm.heat_number) errors.heat_number = t('heats.form.required');
    if (!heatForm.start_time) errors.start_time = t('heats.form.required');
    if (!heatForm.max_participants) errors.max_participants = t('heats.form.required');
    setHeatFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error(t('heats.form.missingFields'));
      return false;
    }
    return true;
  };

  /**
   * La fenêtre d'édition d'une vague s'enregistre au fur et à mesure.
   *
   * Elle ne porte plus que des réglages INTERNES — heure, couloir, épreuve,
   * catégorie : le statut, seul champ qui rendait la vague publique, est parti
   * sur la pastille de la ligne. C'est ce qui rend l'auto-save acceptable ici.
   *
   * ⚠️ L'email de changement d'horaire n'est PAS proposé à chaque blur : il
   * empilerait une confirmation par-dessus la fenêtre encore ouverte. Il est
   * proposé à la FERMETURE, une fois, si l'horaire d'une vague publiée a bougé.
   */
  const notifEnAttente = useRef<any>(null);
  const editingHeatRef = useRef<Heat | null>(null);
  editingHeatRef.current = editingHeat;

  const autoHeat = useAutoSave<Record<string, any>>(useCallback(async (patch) => {
    const id = editingHeatRef.current?.id;
    if (!id) return;
    const res = await apiService.updateHeat(id, patch);
    if (res.data?.pending_schedule_notification) notifEnAttente.current = res.data;
    // Pas de rechargement ici : il remonterait le planning sous la fenêtre à
    // chaque champ quitté. Il a lieu à la fermeture.
  }, []), {
    onError: (error: any) =>
      toast.error(error?.response?.data?.detail || error?.message || t('common.error')),
  });

  /** Fermer la fenêtre : on recharge, et on propose l'email s'il y a lieu. */
  const fermerEditionHeat = () => {
    setEditDialogOpen(false);
    setEditingHeat(null);
    resetHeatForm();
    loadHeats();
    if (notifEnAttente.current) {
      maybePromptScheduleNotify(notifEnAttente.current);
      notifEnAttente.current = null;
    }
  };

  const handleCreateHeat = async () => {
    if (!selectedEvent) return;
    if (!validateHeatForm()) return;

    setLoading(true);
    try {
      const payload: any = {
        event_id: selectedEvent,
        heat_number: parseInt(heatForm.heat_number),
        heat_name: heatForm.heat_name || null,
        start_time: heatForm.start_time,
        end_time: heatForm.end_time || null,
        max_participants: parseInt(heatForm.max_participants.toString()),
        wod_id: heatForm.wod_id || null,
        category_id: heatForm.category_id || null,
        // Une vague naît PLANIFIÉE. Le formulaire posait `ready` par défaut :
        // créer une vague à la main la publiait aussitôt au planning public,
        // sans que rien ne le dise. Publier est une décision, elle se prend à
        // la pastille ou au bouton « Publier ».
        status: 'scheduled'
      };

      if (heatForm.heat_date) {
        payload.heat_date = heatForm.heat_date;
      }

      const response = await apiService.createHeat(payload);
      toast.success(response.data?.message || t('heats.toasts.created'));
      setCreateDialogOpen(false);
      resetHeatForm();
      loadHeats();
    } catch (error: any) {
      console.error('Erreur création heat:', error);
      toast.error(error.response?.data?.detail || error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (heat: Heat) => {
    setEditingHeat(heat);
    setHeatForm({
      heat_number: heat.heat_number?.toString() || '',
      heat_name: heat.heat_name || '',
      heat_date: heat.date || '',
      start_time: heat.start_time || '',
      end_time: heat.end_time || '',
      max_participants: heat.max_participants || 18,
      wod_id: heat.wod_id || '',
      category_id: heat.category_id || '',
    });
    setEditDialogOpen(true);
  };

  const resetHeatForm = () => {
    setHeatFormErrors({});
    setHeatForm({
      heat_number: '',
      heat_name: '',
      heat_date: '',
      start_time: '',
      end_time: '',
      max_participants: 18,
      wod_id: '',
      category_id: '',
    });
  };

  const handleImportExcel = async (file: File) => {
    if (!selectedEvent) return;

    setImportLoading(true);
    try {
      const response = await apiService.importHeats(file, selectedEvent);
      const result = response.data || {};

      toast.success(result.message);
      // Les lignes refusées ne se noient pas dans le succès : elles ont leur
      // propre message, et il reste affiché plus longtemps.
      if (result.errors && result.errors.length > 0) {
        toast.warning(result.errors.join('\n'), { duration: 10000 });
      }
      setImportDialogOpen(false);
      loadHeats();
    } catch (error: any) {
      console.error('Erreur import heats:', error);
      toast.error(error.response?.data?.detail || error.message || t('common.error'));
    } finally {
      setImportLoading(false);
    }
  };

  const handleExportPdf = () => {
    if (!selectedEvent || heats.length === 0) {
      toast.error(t('heats.export.noHeats'));
      return;
    }
    setPdfExportDialogOpen(true);
  };

  const handleConfirmExportPdf = async (selectedDate: string | null) => {
    if (!selectedEvent) return;

    try {
      setLoading(true);
      setPdfExportDialogOpen(false);

      const heatsToExport = selectedDate
        ? heats.filter(h => h.date === selectedDate)
        : heats;

      if (heatsToExport.length === 0) {
        toast.error(t('heats.export.noHeatsForDate'));
        return;
      }

      const heatsWithParticipants = await HeatsPdfService.fetchHeatsWithParticipants(
        selectedEvent,
        heatsToExport
      );

      const estCourse = isCourseEvent(selectedEventData?.event_type);

      // Même PDF pour les deux : la structure est identique, seul le
      // vocabulaire change (« vague »/« heat », « dossard »/« n° équipe »).
      await HeatsPdfService.downloadHeatsPdf({
        eventName: selectedEventData?.name || 'Événement',
        heats: heatsWithParticipants,
        selectedDate: selectedDate || undefined,
        labels: {
          title: estCourse ? t('heats.printWavesTitle') : t('heats.printHeatsTitle'),
          wave: estCourse ? t('heats.waveShort') : t('judgePlan.heat'),
          hour: t('judgePlan.hour'),
          lane: t('heats.colLane'),
          footer: t('heats.printFooter'),
        },
      });
    } catch (error: any) {
      console.error('Erreur export PDF:', error);
      toast.error(t('heats.export.error', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };





  const handleConfirmReseed = async (scope: ReseedScope) => {
    if (!selectedEvent || !selectedReseedDate) return;

    setReseedLoading(true);
    try {
      const response = await heatsReseedService.reseedHeats(selectedEvent, selectedReseedDate, scope);

      setReseedDialogOpen(false);
      await loadHeats();
      toast.success(t('heats.reseedDialog.success', {
        heats: response.reseeded_heats, teams: response.teams_reassigned,
      }));
    } catch (error: any) {
      console.error('Erreur re-seeding:', error);
      toast.error(t('heats.reseedDialog.error', { message: error.message }));
    } finally {
      setReseedLoading(false);
    }
  };

  // Redistribution des heats
  const handleRedistributePreview = async () => {
    if (!selectedEvent) return;

    setRedistributeLoading(true);
    try {
      const requestBody: any = {
        event_id: selectedEvent,
        new_lanes_count: redistributeParams.new_lanes_count,
        allow_category_mixing: redistributeParams.allow_category_mixing,
        preview_only: true
      };
      if (redistributeParams.restart_time) {
        requestBody.restart_time = redistributeParams.restart_time;
      }
      if (redistributeParams.new_heat_gap) {
        requestBody.new_heat_gap = parseInt(redistributeParams.new_heat_gap);
      }

      const data = (await apiService.redistributeHeats(requestBody)).data;
      if (data.success) {
        setRedistributePreview(data);
      } else {
        toast.error(data.error || t('common.error'));
      }
    } catch (error: any) {
      console.error('Erreur simulation redistribution:', error);
      toast.error(error.response?.data?.detail || error.message || t('common.error'));
    } finally {
      setRedistributeLoading(false);
    }
  };

  const handleRedistributeApply = () => {
    if (!selectedEvent || !redistributePreview) return;
    setRedistributeConfirmOpen(true);
  };

  const applyRedistribution = async () => {
    if (!selectedEvent || !redistributePreview) return;
    setRedistributeConfirmOpen(false);
    setRedistributeLoading(true);
    try {
      const requestBody: any = {
        event_id: selectedEvent,
        new_lanes_count: redistributeParams.new_lanes_count,
        allow_category_mixing: redistributeParams.allow_category_mixing,
        preview_only: false
      };
      if (redistributeParams.restart_time) {
        requestBody.restart_time = redistributeParams.restart_time;
      }
      if (redistributeParams.new_heat_gap) {
        requestBody.new_heat_gap = parseInt(redistributeParams.new_heat_gap);
      }

      const data = (await apiService.redistributeHeats(requestBody)).data;
      if (data.success) {
        setRedistributeDialogOpen(false);
        setRedistributePreview(null);
        await loadHeats();
        toast.success(t('heats.toasts.redistributed', {
          teams: data.teams_redistributed, heats: data.new_heats_count,
        }));
      } else {
        toast.error(data.error || t('common.error'));
      }
    } catch (error: any) {
      console.error('Erreur application redistribution:', error);
      toast.error(error.response?.data?.detail || error.message || t('common.error'));
    } finally {
      setRedistributeLoading(false);
    }
  };

  const addDay = () => {
    const lastDay = multiDaySchedule[multiDaySchedule.length - 1];
    const nextDate = new Date(lastDay.date);
    nextDate.setDate(nextDate.getDate() + 1);

    setMultiDaySchedule([...multiDaySchedule, {
      date: nextDate.toISOString().split('T')[0],
      start_time: '09:00',
      end_time: '18:00',
      wod_ids: [],
      breaks: []
    }]);
  };

  const removeDay = (index: number) => {
    setMultiDaySchedule(multiDaySchedule.filter((_, i) => i !== index));
  };

  const updateDay = (index: number, updates: Partial<DaySchedule>) => {
    setMultiDaySchedule(prev => prev.map((day, i) =>
      i === index ? { ...day, ...updates } : day
    ));
  };

  const addBreakToDay = (dayIndex: number) => {
    const newBreak: BreakTime = {
      id: Date.now().toString(),
      start_time: '12:00',
      end_time: '13:00',
      name: 'Pause'
    };
    updateDay(dayIndex, {
      breaks: [...(multiDaySchedule[dayIndex].breaks || []), newBreak]
    });
  };

  const removeDayBreak = (dayIndex: number, breakId: string) => {
    updateDay(dayIndex, {
      breaks: (multiDaySchedule[dayIndex].breaks || []).filter(b => b.id !== breakId)
    });
  };

  const updateDayBreak = (dayIndex: number, breakId: string, updates: Partial<BreakTime>) => {
    updateDay(dayIndex, {
      breaks: (multiDaySchedule[dayIndex].breaks || []).map(b =>
        b.id === breakId ? { ...b, ...updates } : b
      )
    });
  };

  // selectedEventData vient du contexte EventContext

  const estEvenementCourse = isCourseEvent(selectedEventData?.event_type);



  if (loading && heats.length === 0) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Si aucun événement sélectionné, afficher un message
  if (!selectedEvent) {
    return (
      <div className="p-4 md:p-6">
        <Alert>
          <AlertDescription>
            Sélectionne un événement dans le menu latéral pour gérer les heats.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <HeatsToolbar
        counts={{
          wods: wods?.length || 0,
          categories: categories?.length || 0,
          lanes: heats[0]?.max_participants,
          heats: heats.length,
          assignedTeams: assignedTeamsCount,
          publishable: publishableHeats.length,
          futureDates: futureDates.length,
        }}
        flags={{
          isCourseEvent: estEvenementCourse,
          loading,
          publishLoading,
          highlightPublish,
          tourForcePublish,
        }}
        publishButtonRef={publishButtonRef}
        actions={{
          onGenerate: openGenerateDialog,
          onCreate: () => setCreateDialogOpen(true),
          onPublish: handleOpenPublishDialog,
          onAutoAssign: () => setAutoAssignDialogOpen(true),
          onUnassignAll: () => setUnassignAllDialogOpen(true),
          onImport: () => setImportDialogOpen(true),
          onReseed: () => setReseedDialogOpen(true),
          onBulkStatus: () => setBulkStatusOpen(true),
          onRedistribute: openRedistributeDialog,
          onExportPdf: handleExportPdf,
          onDelete: () => setDeleteHeatsOpen(true),
          onHelp: () => startHeatsTour(),
        }}
      />

      {/* Heats Display */}
      {heats.length === 0 ? (
        <HeatsEmptyState
          isCourseEvent={estEvenementCourse}
          canGenerate={(categories?.length || 0) > 0 && (wods?.length || 0) > 0}
          onGenerate={openGenerateDialog}
          onCreate={() => setCreateDialogOpen(true)}
          onImport={() => setImportDialogOpen(true)}
        />
      ) : (
        <HeatGroupedView
          heats={heats}
          wods={wods}
          categories={categories}
          heatParticipants={heatParticipants}
          onReorderDay={reorderDay}
          onTeamDrop={handleTeamDrop}
          onEdit={openEditDialog}
          onDelete={handleDeleteHeat}
          onMoveHeatToDay={openMoveHeatToDay}
          onInsertBreak={setBreakHeat}
          onRemovePause={(heat, minutes) => applyShift(heat, -minutes)}
          onMoveBlockToDay={openMoveBlockToDay}
          isCourseEvent={estEvenementCourse}
          onToggleRegistrationOpen={slotSelectionEnabled ? toggleRegistrationOpen : undefined}
          onChangeStatus={changeHeatStatus}
          eventOver={eventOver}
          sidebar={
            <UnassignedTeamsRail
              teams={teams}
              heats={heats}
              wods={wods}
              heatParticipants={heatParticipants}
              wodFilter={railWodFilter}
              onWodFilterChange={setRailWodFilter}
              categoryFilter={railCategoryFilter}
              onCategoryFilterChange={setRailCategoryFilter}
              search={railSearch}
              onSearchChange={setRailSearch}
            />
          }
        />
      )}

      <BulkStatusDialog
        open={bulkStatusOpen}
        onOpenChange={setBulkStatusOpen}
        heats={heats}
        wodName={heatWodName}
        categoryName={heatCategoryName}
        loading={bulkStatusLoading}
        eventOver={eventOver}
        onConfirm={handleBulkStatus}
      />

      <InsertBreakDialog
        open={breakHeat !== null}
        onOpenChange={(open) => { if (!open) setBreakHeat(null); }}
        heat={breakHeat}
        loading={loading}
        onConfirm={applyBreak}
      />

      <MoveNodeDialog
        plan={pendingMove?.plan ?? null}
        days={moveDays}
        onCancel={() => setPendingMove(null)}
        onConfirm={confirmMove}
      />

      <ConfirmDialog
        open={heatToDelete !== null}
        onOpenChange={(o) => { if (!o) setHeatToDelete(null); }}
        title={t('heats.confirmDelete.title')}
        message={t('heats.confirmDelete.message')}
        confirmLabel={t('common.delete')}
        destructive
        loading={deletingHeat}
        onConfirm={confirmDeleteHeat}
      />

      <ConfirmDialog
        open={redistributeConfirmOpen}
        onOpenChange={setRedistributeConfirmOpen}
        title={t('heats.redistributeDialog.applyConfirmTitle')}
        message={t('heats.redistributeDialog.applyConfirmMessage')}
        confirmLabel={t('heats.actions.apply')}
        destructive
        onConfirm={applyRedistribution}
      />

      <AutoAssignDialog
        open={autoAssignDialogOpen}
        onOpenChange={setAutoAssignDialogOpen}
        heats={heats}
        wods={wods}
        categories={categories}
        onConfirm={runAutoAssign}
      />

      <ConfirmDialog
        open={!!laneSwap}
        onOpenChange={(open) => { if (!open && !laneSwapSaving) setLaneSwap(null); }}
        title={t('heats.laneSwap.title')}
        message={laneSwap ? t('heats.laneSwap.message', {
          a: laneSwap.team.team_name, laneA: laneSwap.cible.lane_number,
          b: laneSwap.cible.team_name, laneB: laneSwap.team.lane_number,
        }) : ''}
        description={
          !laneSwap ? undefined
            : ['preparing', 'countdown', 'running'].includes(laneSwap.statut)
              ? t('heats.laneSwap.live')
              : laneSwap.statut === 'ready'
                ? t('heats.laneSwap.published')
                : t('heats.laneSwap.saved')
        }
        confirmLabel={t('heats.laneSwap.confirm')}
        loading={laneSwapSaving}
        onConfirm={runLaneSwap}
      />

      <ConfirmActionDialog
        open={unassignAllDialogOpen}
        onOpenChange={setUnassignAllDialogOpen}
        title={t('assignments.unassignAllDialog.title')}
        message={t('assignments.unassignAllDialog.confirmMessage', { count: assignedTeamsCount })}
        description={t('assignments.unassignAllDialog.description')}
        confirmLabel={t('assignments.unassignAllDialog.confirm')}
        destructive
        onConfirm={runUnassignAll}
      />

      <GenerateHeatsDialog
        open={generateDialogOpen}
        onOpenChange={setGenerateDialogOpen}
        isCourseEvent={estEvenementCourse}
        courseWaveParams={courseWaveParams}
        onCourseWaveParamsChange={setCourseWaveParams}
        selectedCourse={selectedCourseForGeneration}
        onSelectedCourseChange={setSelectedCourseForGeneration}
        availableCourses={availableCoursesForGeneration}
        categoryOrder={courseCategoryOrder}
        selectedCategories={selectedCategoriesForGeneration}
        onToggleCategory={(id) => setSelectedCategoriesForGeneration(prev =>
          prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
        onMoveCategoryOrder={moveCategoryOrder}
        generateParams={generateParams}
        onGenerateParamsChange={setGenerateParams}
        teamCount={teams.length}
        categories={categories}
        wods={wods}
        days={multiDaySchedule}
        onUpdateDay={updateDay}
        onAddDay={addDay}
        onRemoveDay={removeDay}
        onAddBreak={addBreakToDay}
        onUpdateBreak={updateDayBreak}
        onRemoveBreak={removeDayBreak}
        loading={loading}
        onGenerate={handleGenerateHeats}
      />

      <HeatFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        mode="create"
        form={heatForm}
        errors={heatFormErrors}
        onChange={(update) => { setHeatFormErrors({}); setHeatForm(update); }}
        wods={wods}
        categories={categories}
        loading={loading}
        onSubmit={handleCreateHeat}
        onCancel={() => { setCreateDialogOpen(false); resetHeatForm(); }}
      />

      <HeatFormDialog
        open={editDialogOpen}
        onOpenChange={(o) => { if (!o) fermerEditionHeat(); }}
        mode="edit"
        form={heatForm}
        errors={heatFormErrors}
        onChange={(update) => { setHeatFormErrors({}); setHeatForm(update); }}
        wods={wods}
        categories={categories}
        loading={loading}
        auto={autoHeat}
        onCancel={fermerEditionHeat}
      />

      <ImportHeatsDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onFileSelected={handleImportExcel}
      />

      <ExportPdfDialog
        open={pdfExportDialogOpen}
        onOpenChange={setPdfExportDialogOpen}
        heats={heats}
        onExport={handleConfirmExportPdf}
      />

      <ReseedHeatsDialog
        open={reseedDialogOpen}
        onOpenChange={setReseedDialogOpen}
        heats={heats}
        futureDates={futureDates}
        selectedDate={selectedReseedDate}
        onSelectDate={setSelectedReseedDate}
        loading={reseedLoading}
        onConfirm={handleConfirmReseed}
      />

      <RedistributeHeatsDialog
        open={redistributeDialogOpen}
        onOpenChange={setRedistributeDialogOpen}
        params={redistributeParams}
        onParamsChange={setRedistributeParams}
        preview={redistributePreview}
        loading={redistributeLoading}
        onSimulate={handleRedistributePreview}
        onApply={handleRedistributeApply}
      />

      <DeleteHeatsDialog
        open={deleteHeatsOpen}
        onOpenChange={setDeleteHeatsOpen}
        heats={heats}
        wodName={heatWodName}
        categoryName={heatCategoryName}
        loading={deleteHeatsLoading}
        eventOver={eventOver}
        onConfirm={handleDeleteHeats}
      />

      <GenerateErrorDialog
        open={generateErrorDialogOpen}
        onOpenChange={setGenerateErrorDialogOpen}
        error={generateError}
        isCourseEvent={estEvenementCourse}
      />

      <PublishHeatsDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        filteredHeats={filteredPublishableHeats}
        uniqueWods={uniqueWodsInPublishable}
        uniqueCategories={uniqueCategoriesInPublishable}
        wodFilter={publishWodFilter}
        onWodFilterChange={setPublishWodFilter}
        categoryFilter={publishCategoryFilter}
        onCategoryFilterChange={setPublishCategoryFilter}
        selectedIds={selectedHeatsToPublish}
        onToggleHeat={toggleHeatSelection}
        onToggleAll={toggleAllHeatsSelection}
        effectiveSelectedIds={effectiveSelectedHeatIds}
        loading={previewLoading}
        onRequestPublish={handleRequestPublish}
      />

      <ConfirmPublishDialog
        open={confirmPublishOpen}
        onOpenChange={setConfirmPublishOpen}
        preview={publishPreview}
        loading={publishLoading}
        onConfirm={confirmPublishHeats}
      />

      <ScheduleNotifyDialog
        open={scheduleNotifyOpen}
        onOpenChange={setScheduleNotifyOpen}
        recipients={scheduleNotify?.recipients ?? 0}
        loading={scheduleNotifyLoading}
        onConfirm={confirmScheduleNotify}
        onLater={() => { setScheduleNotifyOpen(false); setScheduleNotify(null); }}
      />

    </div>
  );
};

export default Heats;
